import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rolesApi } from '../../../api/admin/roles';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { X } from 'lucide-react';
import { DataTable } from '../../../components/DataTable';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { PermissionScope } from '../../../types/admin';

const scopes: PermissionScope[] = ['own', 'project', 'department', 'location', 'country', 'all'];

const formSchema = z.object({
  permissionKey: z.string().min(3, 'Enter a permission key, e.g. admin.departments.manage'),
  scope: z.enum(['own', 'project', 'department', 'location', 'country', 'all']),
});
type FormValues = z.infer<typeof formSchema>;

export function RolePermissionsPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const id = Number(roleId);
  const canManage = usePermission('admin.permissions.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const permissionsQuery = useQuery({ queryKey: ['role-permissions', id], queryFn: () => rolesApi.permissions(id) });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { scope: 'own' },
  });

  const grantMutation = useMutation({
    mutationFn: (values: FormValues) => rolesApi.grantPermission(id, values),
    onSuccess: () => {
      showSuccess('Permission granted.');
      queryClient.invalidateQueries({ queryKey: ['role-permissions', id] });
      reset();
      setShowForm(false);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to grant permission.'),
  });

  const revokeMutation = useMutation({
    mutationFn: (permissionKey: string) => rolesApi.revokePermission(id, permissionKey),
    onSuccess: () => {
      showSuccess('Permission revoked.');
      queryClient.invalidateQueries({ queryKey: ['role-permissions', id] });
    },
    // Surfaces the last-admin-capable-role guard's real message if this
    // was the only role still granting admin.users.manage.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to revoke permission.'),
  });

  return (
    <div className="page">
      <Link to="/admin/roles">&larr; Back to Roles</Link>
      <div className="page-header">
        <h1>Role Permissions</h1>
        {canManage && <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Grant Permission'}</button>}
      </div>

      {showForm && (
        <form className="inline-form" onSubmit={handleSubmit((values) => grantMutation.mutate(values))}>
          <label>
            Permission key
            <input placeholder="e.g. admin.departments.manage" {...register('permissionKey')} />
            {errors.permissionKey && <span className="field-error">{errors.permissionKey.message}</span>}
          </label>
          <label>
            Scope
            <select {...register('scope')}>
              {scopes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={grantMutation.isPending}>{grantMutation.isPending ? 'Saving…' : 'Grant'}</button>
        </form>
      )}

      {permissionsQuery.isLoading && <p>Loading…</p>}
      {permissionsQuery.isError && <p className="error-text">Could not load permissions.</p>}

      {permissionsQuery.data && (
        <DataTable
          rows={permissionsQuery.data.data}
          rowKey={(p) => p.id}
          emptyMessage="This role has no permissions yet."
          columns={[
            { header: 'Permission', render: (p) => p.permission_key },
            { header: 'Scope', render: (p) => p.scope },
            {
              header: '',
              render: (p) =>
                canManage ? (
                  <IconActionButton icon={X} label="Revoke" danger onClick={() => revokeMutation.mutate(p.permission_key)} />
                ) : null,
            },
          ]}
        />
      )}
    </div>
  );
}
